import {
  getStatus,
  isOk,
  MedplumClient,
  type MedplumClientOptions,
  type MedplumRequestOptions,
  normalizeOperationOutcome,
  OperationOutcomeError,
  type ReadHistoryOptions,
  sleep,
  type WithId,
} from '@medplum/core'
import type {
  Bundle,
  BundleEntry,
  ExtractResource,
  Parameters as FhirParameters,
  OperationOutcome,
  Practitioner,
  ProjectMembership,
  Reference,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import { cloneDeep, isEqual } from 'lodash-es'
import {
  BATCH_ENTRY_BASE_VERSION_EXTENSION,
  TRACE_ID_SYSTEM,
} from '#src/codeSystems.ts'
import { base64Decode, base64Encode } from '#src/encoding.ts'
import {
  ExtendedResource,
  type ExtendedResourceConstructor,
  type ResourceOf,
} from '#src/ExtendedResource.ts'
import {
  fhirPathPatchToJsonPatch,
  getFhirPathPatchSidecars,
  isFhirPathPatchParameters,
} from '#src/fhirpath-patch/index.ts'
import { invariant } from '#src/invariant.ts'
import {
  type DeepPushOnlyArray,
  monitorResource,
  type PatchOperation,
} from '#src/monitorResource.ts'
import { patchEntryBaseVersion } from '#src/queued-edits/applied-from-versions.ts'
import {
  createReferenceVersionUrl,
  parseHistoryReferenceString,
} from '#src/references.ts'
import { sanitizeOutboundFhirBody } from '#src/sanitizeOutboundFhirBody.ts'
import {
  normalizeQuery,
  type QueryTypes,
  queryTypesToString,
} from '#src/search.ts'
import { addTag } from '#src/tags.ts'
import { extractId, getBinaryId } from '#src/utils.ts'
import { getPreviousVersionFromHistory } from '#src/utilsExtendedResource.ts'

// Medplum rejects cursor pagination below this page size.
const MIN_CURSOR_PAGE_SIZE = 20

type Constructor<T = object> = new (...args: any[]) => T

interface BundleErrorInfo {
  index: number
  outcome: OperationOutcome
  entry?: BundleEntry
}

export interface RateLimitRetryOptions {
  /** Maximum number of retries on 429 responses. Default: 3 */
  maxRetries?: number
  /** Initial backoff delay in ms before exponential increase. Default: 1000 */
  initialDelay?: number
  /** Maximum backoff delay in ms. Default: 30000 */
  maxDelay?: number
}

export interface BundleRetryEntryAttribution {
  resourceType: string
  resourceId: string | undefined
}

export interface BundleRetryEvent {
  attempt: number
  entries: BundleRetryEntryAttribution[]
}

export interface SeenMedplumClientOptions extends MedplumClientOptions {
  traceId?: string
  /** Enable automatic retry with backoff on 429 rate-limit responses. */
  rateLimitRetry?: RateLimitRetryOptions | boolean
  /**
   * Fires when `executeBatch` retries a transaction after a versionId test
   * failure. Used to surface the retry in observability (e.g. RUM) from
   * packages that can import a browser-only tracker — `@seen/core` cannot
   * import `@datadog/browser-rum` directly.
   */
  onBundleRetry?: (event: BundleRetryEvent) => void
}

/**
 * EXPERIMENTAL — read all caveats before enabling. Opt-in retry semantics
 * for mutation calls (`patchResourceProxy`, `patchResourceInstance`) that
 * race against concurrent writers. The double-underscore prefix is
 * intentional: it signals "you must understand the failure modes below
 * before turning this on" and makes call sites grep-able for review.
 *
 * # Behavior
 *
 * On a `412 Conflict` (versionId test failure) that the underlying
 * `patchResource` retry path could not auto-resolve via
 * `arePatchOperationsSafe`, re-read the latest version of the resource,
 * re-run the caller's `mutateFn` against that fresh copy, and retry the
 * patch. Optimistic concurrency is preserved on each attempt — every retry
 * issues a fresh `test /meta/versionId` op against the re-read version.
 * Bounded by `__experimental_refetchOnConflictMaxAttempts` (default 3).
 *
 * # When to use
 *
 * `mutateFn` is *idempotent and intent-based* AND any conditional logic
 * that decides whether to mutate lives INSIDE the callback. The retry
 * re-runs only the callback against fresh state — nothing outside the
 * callback gets re-evaluated.
 *
 * Examples that are safe:
 * - `(o) => { o.markSubmissionSucceeded() }` — unconditional terminal set
 * - `(t) => { if (t.state === 'pending') t.state = 'in_progress' }` —
 *   conditional INSIDE the callback. Empty ops on retry → no-op patch,
 *   instance returned unchanged.
 *
 * # Caveats — read carefully before enabling
 *
 * 1. **Outer-guard lost-write hazard.** Conditional logic outside the
 *    callback is invisible to the retry. This pattern is UNSAFE:
 *
 *    ```ts
 *    // BAD — outer guard not re-evaluated on refetch retry
 *    if (task.state === 'pending') {
 *      await patchResourceInstance(task, (t) => { t.state = 'in_progress' },
 *        { __experimental_refetchOnConflict: true })
 *    }
 *    ```
 *
 *    On conflict, the callback runs against the latest task and
 *    unconditionally sets `state='in_progress'`, even if a concurrent
 *    writer has since moved it to `completed`. Move the guard INSIDE the
 *    callback, or do not enable this option.
 *
 * 2. **Side-effects in `mutateFn` run multiple times.** Logging,
 *    counters, external calls inside the callback fire once per attempt.
 *    Keep callbacks pure.
 *
 * 3. **Latency amplification.** Each retry adds a read + patch round
 *    trip. Subscription Lambda timeouts can compound; `patchResource`
 *    itself does up to 3 inline retries before this outer loop kicks in,
 *    so worst-case attempts ≈ outer × inner.
 *
 * 4. **Persistent contention surfaces eventually.** If the same paths
 *    keep colliding, the outer loop exhausts its budget and throws
 *    `Failed to refetch-and-retry patch after N attempts due to
 *    persistent concurrent modifications` (with the original 412
 *    preserved as `cause`). Caller must still handle this.
 *
 * 5. **Non-conflict errors pass through unchanged.** Network, validation,
 *    and auth errors are not retried — only versionId test failures.
 *
 * # Defaults
 *
 * - `__experimental_refetchOnConflict`: `false` (strict-conflict behavior)
 * - `__experimental_refetchOnConflictMaxAttempts`: `3`
 */
export interface RefetchOnConflictOptions {
  __experimental_refetchOnConflict?: boolean
  __experimental_refetchOnConflictMaxAttempts?: number
}

export interface SeenMedplumClientExtension {
  patchResource<K extends ResourceType>(
    resourceType: K,
    id: string,
    operations: PatchOperation<K>[],
    options?: MedplumRequestOptions
  ): Promise<WithId<ExtractResource<K>>>

  patchResourceProxy<K extends ResourceType>(
    resource: ExtractResource<K>,
    mutateFn: (resource: DeepPushOnlyArray<ExtractResource<K>>) => void,
    options?: MedplumRequestOptions & RefetchOnConflictOptions
  ): Promise<ExtractResource<K>>

  patchResourceInstance<T extends ExtendedResource>(
    instance: T,
    mutateFn: (instance: T) => void,
    options?: MedplumRequestOptions &
      RefetchOnConflictOptions & {
        skipVersionCheck?: boolean
      }
  ): Promise<T>

  getPreviousVersion<T extends Resource>(
    resource: T | ExtendedResource<T>
  ): Promise<T | undefined>

  readResourceClass<T extends ExtendedResource>(
    resourceClass: ExtendedResourceConstructor<T>,
    id?: Reference<NoInfer<ResourceOf<T>>> | string | null,
    options?: MedplumRequestOptions
  ): Promise<T>

  readReferenceClass<T extends ExtendedResource>(
    resourceClass: ExtendedResourceConstructor<T>,
    reference: Reference<NoInfer<ResourceOf<T>>>,
    options?: MedplumRequestOptions
  ): Promise<T>

  readHistoryClass<T extends ExtendedResource>(
    resourceClass: ExtendedResourceConstructor<T>,
    id: string,
    options?: ReadHistoryOptions
  ): Promise<T[]>

  readVersionClass<T extends ExtendedResource>(
    resourceClass: ExtendedResourceConstructor<T>,
    id: string,
    versionId: string,
    options?: MedplumRequestOptions
  ): Promise<T>

  searchResourceClass<T extends ExtendedResource>(
    resourceClass: ExtendedResourceConstructor<T>,
    query: QueryTypes | undefined,
    options?: MedplumRequestOptions
  ): Promise<T[]>

  searchOneResourceClass<T extends ExtendedResource>(
    resourceClass: ExtendedResourceConstructor<T>,
    query: QueryTypes | undefined,
    options?: MedplumRequestOptions
  ): Promise<T | null>

  searchResourceClassPages<T extends ExtendedResource>(
    resourceClass: ExtendedResourceConstructor<T>,
    query: QueryTypes | undefined,
    options?: MedplumRequestOptions
  ): AsyncGenerator<T[]>

  searchResourceClassPagesAll<T extends ExtendedResource>(
    resourceClass: ExtendedResourceConstructor<T>,
    query: QueryTypes | undefined,
    options?: MedplumRequestOptions
  ): Promise<T[]>

  // Use cursor pagination when each write drops the resource from the search
  // results; offset paging would skip rows as the result set shrinks. Do not
  // use it when writes leave resources matched: bumping _lastUpdated moves a
  // processed row behind the cursor, so it can be returned again. Medplum
  // requires _sort=_lastUpdated ascending and _count >= 20, both of which this
  // applies for you.
  searchResourceClassPagesCursor<T extends ExtendedResource>(
    resourceClass: ExtendedResourceConstructor<T>,
    query: QueryTypes | undefined,
    options?: MedplumRequestOptions
  ): AsyncGenerator<T[]>

  executeBatch(bundle: Bundle): Promise<Bundle>

  downloadAttachmentAsBinary(
    url: string,
    options?: MedplumRequestOptions
  ): Promise<Blob>

  upsertResourceClass<T extends ExtendedResource>(
    instance: T,
    query: QueryTypes,
    options?: MedplumRequestOptions
  ): Promise<T>

  createResourceClass<T extends ExtendedResource>(
    instance: T,
    options?: MedplumRequestOptions
  ): Promise<T>

  createResourceClassIfNoneExist<T extends ExtendedResource>(
    instance: T,
    query: QueryTypes,
    options?: MedplumRequestOptions
  ): Promise<T>

  onBehalfOf<T>(
    email: string,
    action: (projectMembership: ProjectMembership) => Promise<T>
  ): Promise<T>

  searchProjectMembershipByPractitionerEmail(email: string): Promise<
    | {
        membership: WithId<ProjectMembership>
        practitioner: WithId<Practitioner>
      }
    | undefined
  >

  /**
   * Central request method that all HTTP methods delegate through.
   * Handles rate-limit (429) retry with exponential backoff when configured.
   */
  requestWithRetry<T>(fn: () => Promise<T>): Promise<T>

  get traceId(): string | undefined
}

function isRateLimitError(cause: unknown): boolean {
  if (cause instanceof OperationOutcomeError) {
    return getStatus(cause.outcome) === 429
  }
  const outcome = normalizeOperationOutcome(cause)
  return getStatus(outcome) === 429
}

const DEFAULT_RATE_LIMIT_MAX_RETRIES = 3
const DEFAULT_RATE_LIMIT_INITIAL_DELAY = 1000
const DEFAULT_RATE_LIMIT_MAX_DELAY = 60_000

function resolveRateLimitOptions(
  opt: RateLimitRetryOptions | boolean | undefined
): Required<RateLimitRetryOptions> | undefined {
  if (!opt) {
    return undefined
  }
  const cfg = opt === true ? {} : opt
  return {
    maxRetries: cfg.maxRetries ?? DEFAULT_RATE_LIMIT_MAX_RETRIES,
    initialDelay: cfg.initialDelay ?? DEFAULT_RATE_LIMIT_INITIAL_DELAY,
    maxDelay: cfg.maxDelay ?? DEFAULT_RATE_LIMIT_MAX_DELAY,
  }
}

export function SeenMedplumClientMixin<
  TBase extends Constructor<MedplumClient>,
>(Base: TBase): TBase & Constructor<SeenMedplumClientExtension> {
  return class SeenMedplumClientMixinClass
    extends Base
    implements SeenMedplumClientExtension
  {
    private _traceId?: string
    private _rateLimitRetry?: Required<RateLimitRetryOptions>
    private _onBundleRetry?: (event: BundleRetryEvent) => void

    constructor(...args: any[]) {
      super(...args)
      this._traceId = args[0]?.traceId
      this._rateLimitRetry = resolveRateLimitOptions(args[0]?.rateLimitRetry)
      this._onBundleRetry = args[0]?.onBundleRetry
    }

    /**
     * Returns the delay in ms to wait before the next request, capped at
     * `maxDelay`. For each exhausted rate limit (`remainingUnits === 0`),
     * computes the time until it resets and takes the max across all
     * exhausted limits and `fallback`.
     */
    private getRateLimitDelay(fallback: number, maxDelay: number): number {
      let delay = fallback
      const nowSec = Date.now() / 1000
      for (const limit of this.rateLimitStatus()) {
        if (limit.remainingUnits === 0) {
          const secs = Math.max(0, limit.resetsAfter - nowSec)
          delay = Math.max(delay, secs * 1000)
        }
      }
      return Math.min(delay, maxDelay)
    }

    /**
     * Central request method that all HTTP methods delegate through.
     * Handles rate-limit (429) retry with exponential backoff when configured.
     * Checks rateLimitStatus() before each attempt and waits if already exhausted.
     */
    async requestWithRetry<T>(fn: () => Promise<T>): Promise<T> {
      const cfg = this._rateLimitRetry
      if (!cfg) {
        return fn()
      }

      for (let attempt = 0; attempt <= cfg.maxRetries; attempt++) {
        // Wait if we already know we're rate-limited before making the request
        const preDelay = this.getRateLimitDelay(0, cfg.maxDelay)
        if (preDelay > 0) {
          await sleep(preDelay)
        }

        try {
          return await fn()
        } catch (error) {
          if (!isRateLimitError(error) || attempt === cfg.maxRetries) {
            throw error
          }

          // Use the server-reported secondsUntilReset from exhausted rate limits,
          // falling back to exponential backoff
          const postDelay = this.getRateLimitDelay(
            cfg.initialDelay * 2 ** attempt,
            cfg.maxDelay
          )
          await sleep(postDelay)
        }
      }

      // Unreachable — the loop always returns or throws
      throw new Error('Unexpected end of rate limit retry loop')
    }

    get traceId(): string | undefined {
      return this._traceId
    }

    async put(
      url: URL | string,
      body: any,
      contentType: string,
      options?: MedplumRequestOptions
    ): ReturnType<MedplumClient['put']> {
      if (this._traceId) {
        addTag(body, { system: TRACE_ID_SYSTEM, code: this._traceId })
      }
      sanitizeOutboundFhirBody(body)
      return this.requestWithRetry(() =>
        super.put(url, body, contentType, options)
      )
    }

    async post<T = any>(
      url: URL | string,
      body?: any,
      contentType?: string,
      options?: MedplumRequestOptions
    ): Promise<T> {
      if (this._traceId) {
        addTag(body, { system: TRACE_ID_SYSTEM, code: this._traceId })
      }
      sanitizeOutboundFhirBody(body)
      return this.requestWithRetry(() =>
        super.post<T>(url, body, contentType, options)
      )
    }

    async patch(
      url: URL | string,
      body: any,
      options?: MedplumRequestOptions
    ): ReturnType<MedplumClient['patch']> {
      if (this._traceId) {
        addTag(body, { system: TRACE_ID_SYSTEM, code: this._traceId })
      }
      sanitizeOutboundFhirBody(body)
      return this.requestWithRetry(() => super.patch(url, body, options))
    }

    async delete(
      url: URL | string,
      options?: MedplumRequestOptions
    ): ReturnType<MedplumClient['delete']> {
      return this.requestWithRetry(() => super.delete(url, options))
    }

    async executeBatch(batch: Bundle): Promise<Bundle> {
      if (batch.entry?.length === 0) {
        return Promise.resolve(batch)
      }

      if (this._traceId) {
        addTag(batch, {
          system: TRACE_ID_SYSTEM,
          code: this._traceId,
        })
      }

      // Must run first: everything downstream (sanitize, retry,
      // super.executeBatch) only ever sees Binary json-patch entries.
      await this.convertFhirPathEntriesToJsonPatch(batch)

      sanitizeOutboundFhirBody(batch)

      const maxRetries = 3
      const retryDelay = 100

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const result = await super.executeBatch(batch)

          if (result.entry?.length) {
            const errors: BundleErrorInfo[] = []
            result.entry?.forEach((r, i) => {
              if (r.response?.outcome && !isOk(r.response.outcome)) {
                errors.push({
                  index: i,
                  outcome: r.response.outcome,
                  entry: batch.entry?.[i],
                })
              }
            })
            if (errors.length > 0) {
              throw new SeenMedplumBatchError('Batch operation failed', errors)
            }
          }
          stampBaseVersions(batch, result)
          return result
        } catch (error) {
          if (batch.type !== 'transaction' || attempt >= maxRetries) {
            throw error
          }

          const patchEntries = findVersionTestFailedPatchEntries(error, batch)
          if (patchEntries.length === 0) {
            throw error
          }

          let allSafe = true
          for (const entry of patchEntries) {
            const rebuilt = await this.rebuildPatchEntryOps(entry)
            if (!rebuilt) {
              allSafe = false
              break
            }
          }

          if (!allSafe) {
            throw error
          }

          const retryEntries = patchEntries
            .map(bundleEntryAttribution)
            .filter((a): a is BundleRetryEntryAttribution => a !== undefined)
          console.warn(
            `[fhir] bundle transaction retry attempt=${attempt + 1}, ` +
              `entries=${retryEntries.length}`
          )
          this._onBundleRetry?.({
            attempt: attempt + 1,
            entries: retryEntries,
          })

          await sleep(retryDelay * 2 ** attempt * (0.75 + Math.random() * 0.5))
        }
      }

      throw new Error('Unexpected end of executeBatch retry loop')
    }

    // Converts fhirpath-patch `Parameters` PATCH entries (emitted by
    // BundleMutationBuilder with patchFormat 'fhirpath-patch') into Binary
    // json-patch in place: load the exact base version named by the
    // `__seenBaseRef` history-ref sidecar, apply the fhirpath patch to it, diff
    // to concrete pointer ops, and prepend the `test /meta/versionId`
    // precondition. The base resource is no longer embedded in the bundle — it
    // is read on demand here (de-duped across entries). Medplum does not accept
    // fhirpath patch at the transport layer yet — delete along with
    // packages/core/src/fhirpath-patch/ when it does.
    private async convertFhirPathEntriesToJsonPatch(
      batch: Bundle
    ): Promise<void> {
      const fhirPathEntries = (batch.entry ?? []).filter(
        (entry) =>
          entry.request?.method === 'PATCH' &&
          isFhirPathPatchParameters(entry.resource)
      )
      if (fhirPathEntries.length === 0) {
        return
      }

      // Load each distinct base version once.
      const baseRefs = new Set<string>()
      for (const entry of fhirPathEntries) {
        const { baseRef } = getFhirPathPatchSidecars(
          entry.resource as FhirParameters
        )
        if (!baseRef) {
          throw new Error(
            `fhirpath-patch entry for ${entry.request?.url} is missing its base history-ref sidecar`
          )
        }
        baseRefs.add(baseRef)
      }

      const baseByRef = new Map<string, Resource>()
      await Promise.all(
        [...baseRefs].map(async (baseRef) => {
          const { resourceType, id, versionId } =
            parseHistoryReferenceString(baseRef)
          try {
            const base = await this.readVersion(
              resourceType as ResourceType,
              id,
              versionId
            )
            baseByRef.set(baseRef, base)
          } catch (error) {
            throw new Error(
              `Failed to load base version ${baseRef} for fhirpath-patch conversion: ${
                error instanceof Error ? error.message : String(error)
              }`
            )
          }
        })
      )

      for (const entry of fhirPathEntries) {
        const parameters = entry.resource as FhirParameters
        const { baseRef, versionId } = getFhirPathPatchSidecars(parameters)
        const baseResource = baseRef ? baseByRef.get(baseRef) : undefined
        invariant(
          baseResource,
          `Missing loaded base resource for ${entry.request?.url}`
        )
        const operations = fhirPathPatchToJsonPatch(parameters, baseResource, {
          versionId,
        })
        entry.resource = {
          resourceType: 'Binary',
          contentType: 'application/json-patch+json',
          data: base64Encode(JSON.stringify(operations)),
        }
      }
    }

    // Re-reads the resource at `fromVersionId` and the latest, then asks
    // `arePatchOperationsSafe` whether the ops conflict with concurrent
    // writes. On success, returns the filtered non-test ops + the fresh
    // versionId to build a new test op against.
    //
    // Shared by the two retry sites that react to a `test /meta/versionId`
    // failure: `patchResource` (single-resource) and `rebuildPatchEntryOps`
    // (bundle entry).
    private async prepareVersionConflictRetry<T extends ResourceType>(
      resourceType: T,
      id: string,
      fromVersionId: string,
      operations: PatchOperation<T>[]
    ): Promise<
      | { safe: true; latestVersionId: string; operations: PatchOperation<T>[] }
      | { safe: false; conflictingPaths?: string[] }
    > {
      const nonTestOps = operations.filter(
        (op) => op.op !== 'test'
      ) as PatchOperation<T>[]
      // A test-only patch is an external transaction guard. Rebasing it would
      // discard the assertion and let the other transaction entries proceed.
      if (nonTestOps.length === 0) {
        return { safe: false }
      }

      // The same reasoning, for a patch that states a precondition of its own
      // beyond the version. A rebase keeps only `nonTestOps`, so such an
      // assertion is dropped and the write is then applied to a version that
      // was never checked against it — the caller asked "only if this still
      // holds" and would get the write anyway. `arePatchOperationsSafe` cannot
      // stand in for it either: it compares the paths the write TOUCHES, and a
      // precondition is about a path the write deliberately does not touch.
      //
      // Refusing the rebase surfaces the original test failure, which is what a
      // caller guarding a clinical invariant wants: `update_participant_note`
      // asserts `/attester` is absent so a note signed mid-write is not
      // overwritten, and a rebase would have silently overwritten it.
      const callerPreconditions = operations.filter(
        (op) => op.op === 'test' && op.path !== '/meta/versionId'
      )
      if (callerPreconditions.length > 0) {
        return { safe: false }
      }

      const [latest, current] = await Promise.all([
        this.readResource(resourceType, id),
        this.readVersion(resourceType, id, fromVersionId),
      ])
      if (!latest || !current) {
        return { safe: false }
      }

      const safetyResult = arePatchOperationsSafe({
        current,
        latest,
        operations: nonTestOps,
      })
      if (!safetyResult.safe) {
        return { safe: false, conflictingPaths: safetyResult.conflictingPaths }
      }

      const latestVersionId = latest.meta?.versionId
      if (!latestVersionId) {
        return { safe: false }
      }

      return {
        safe: true,
        latestVersionId,
        operations: safetyResult.operations as PatchOperation<T>[],
      }
    }

    // Rebuilds a failed bundle PATCH entry against the latest version
    // using `prepareVersionConflictRetry`. Returns true if the entry was
    // successfully rebuilt, false if ops conflict or anything required
    // is missing.
    private async rebuildPatchEntryOps(entry: BundleEntry): Promise<boolean> {
      const url = entry.request?.url
      if (!url) {
        return false
      }
      const [resourceType, id] = url.split('/') as [ResourceType, string]
      if (!resourceType || !id) {
        return false
      }

      const binary = entry.resource
      if (
        binary?.resourceType !== 'Binary' ||
        !binary.data ||
        binary.contentType !== 'application/json-patch+json'
      ) {
        return false
      }

      let ops: PatchOperation<ResourceType>[]
      try {
        ops = JSON.parse(base64Decode(binary.data))
      } catch {
        return false
      }

      const testOp = ops.find(
        (op) => op.op === 'test' && op.path === '/meta/versionId'
      )
      if (!testOp || typeof testOp.value !== 'string') {
        return false
      }

      const decision = await this.prepareVersionConflictRetry(
        resourceType,
        id,
        testOp.value,
        ops
      )
      if (!decision.safe) {
        return false
      }

      const updatedOps: PatchOperation<ResourceType>[] = [
        {
          op: 'test',
          path: '/meta/versionId',
          value: decision.latestVersionId,
        },
        ...decision.operations,
      ]
      binary.data = base64Encode(JSON.stringify(updatedOps))

      return true
    }

    patchResource: SeenMedplumClientExtension['patchResource'] = async (
      resourceType,
      id,
      operations,
      options
    ) => {
      const maxRetries = 3
      const retryDelay = 100 // ms
      const resourceRef = `${resourceType}/${id}`

      let operationsCopy = cloneDeep(operations)
      sanitizeOutboundFhirBody(operationsCopy)

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const result = await super.patchResource(
            resourceType,
            id,
            operationsCopy,
            options
          )
          return result
        } catch (error) {
          const outcome = normalizeOperationOutcome(error)

          const hasTestFailure = outcome.issue?.some(
            (issue) =>
              issue.code === 'invalid' &&
              issue.details?.text?.match(/^test failed/i)
          )
          if (!hasTestFailure) {
            throw error
          }

          const testOperation = operationsCopy.find(
            (op) => op.op === 'test' && op.path === '/meta/versionId'
          )
          if (!testOperation) {
            throw error
          }

          const testOperationVersionId = testOperation.value
          if (
            !testOperationVersionId ||
            typeof testOperationVersionId !== 'string'
          ) {
            throw error
          }

          const decision = await this.prepareVersionConflictRetry(
            resourceType,
            id,
            testOperationVersionId,
            operationsCopy as PatchOperation<typeof resourceType>[]
          )
          if (!decision.safe) {
            if (decision.conflictingPaths) {
              const conflictError = new Error(
                `[${resourceRef}] Patch operations conflict with concurrent changes. ` +
                  `Conflicting paths: ${decision.conflictingPaths.join(', ')}`
              )
              conflictError.cause = error
              throw conflictError
            }
            // Read failure or missing versionId — can't safely retry.
            throw error
          }

          if (attempt === maxRetries) {
            const maxRetriesError = new Error(
              `[${resourceRef}] Failed to apply patch after ${maxRetries + 1} attempts due to concurrent modifications`
            )
            maxRetriesError.cause = error
            throw maxRetriesError
          }

          operationsCopy = [
            {
              op: 'test',
              path: '/meta/versionId',
              value: decision.latestVersionId,
            },
            ...decision.operations,
          ]

          if (attempt < maxRetries) {
            await sleep(
              retryDelay * 2 ** attempt * (0.75 + Math.random() * 0.5)
            )
          }
        }
      }

      throw new Error(`[${resourceRef}] Unexpected end of retry loop`)
    }

    patchResourceProxy: SeenMedplumClientExtension['patchResourceProxy'] =
      async (
        resource,
        mutateFn,
        {
          __experimental_refetchOnConflict: refetchOnConflict = false,
          __experimental_refetchOnConflictMaxAttempts:
            refetchOnConflictMaxAttempts = 3,
          ...options
        } = {}
      ) => {
        invariant(resource.id, 'Resource must have an id')

        // Build patch ops from a fresh `monitorResource` proxy each attempt:
        // because `mutateFn` operates on a snapshot, re-running it against
        // the latest version regenerates ops that already account for the
        // bot/etc.'s concurrent writes (those changes are now part of the
        // baseline, so the proxy only diffs OUR intended mutation against
        // them). The `test /meta/versionId` op preserves optimistic
        // concurrency across retries — we never blindly overwrite.
        const buildAndPatch = async (
          source: ExtractResource<typeof resource.resourceType>
        ): Promise<ExtractResource<typeof resource.resourceType>> => {
          const monitored = monitorResource(source)
          mutateFn(monitored.proxy)

          const ops = monitored.patches
          if (ops.length === 0) {
            return source
          }

          ops.unshift({
            op: 'test',
            path: '/meta/versionId',
            value: source.meta?.versionId,
          })

          return this.patchResource(
            resource.resourceType,
            resource.id as string,
            ops,
            options
          )
        }

        return runWithRefetchOnConflict(
          () => buildAndPatch(resource),
          async () => {
            const fresh = await this.readResource(
              resource.resourceType,
              resource.id as string
            )
            return buildAndPatch(
              fresh as ExtractResource<typeof resource.resourceType>
            )
          },
          {
            enabled: refetchOnConflict,
            maxAttempts: refetchOnConflictMaxAttempts,
            resourceRef: `${resource.resourceType}/${resource.id}`,
          }
        )
      }

    patchResourceInstance: SeenMedplumClientExtension['patchResourceInstance'] =
      async (
        instance,
        mutateFn,
        {
          skipVersionCheck,
          __experimental_refetchOnConflict: refetchOnConflict = false,
          __experimental_refetchOnConflictMaxAttempts:
            refetchOnConflictMaxAttempts = 3,
          ...options
        } = {}
      ) => {
        // Build patch ops by mutating a fresh instance each attempt: when
        // `mutateFn` is intent-based ("set state=success"), re-running it
        // against the latest version regenerates the correct ops relative
        // to whatever interleaved writes have landed since. The
        // `test /meta/versionId` op preserves optimistic concurrency on
        // every attempt — we never blindly overwrite.
        const Ctor = instance.constructor as ExtendedResourceConstructor<any>

        const buildAndPatch = async (
          source: typeof instance
        ): Promise<typeof instance> => {
          const ops = source.mutate(mutateFn)

          if (ops.length === 0) {
            return source
          }

          if (!skipVersionCheck) {
            ops.unshift({
              op: 'test',
              path: '/meta/versionId',
              value: source.versionId,
            })
          }

          const result = await this.patchResource(
            source.fhirType,
            source.id,
            ops,
            options
          )
          return Ctor.fromFhirResource(
            result as ResourceOf<typeof instance>
          ) as typeof instance
        }

        return runWithRefetchOnConflict(
          () => buildAndPatch(instance),
          async () => {
            const fresh = await this.readReferenceClass(
              Ctor,
              instance.reference
            )
            return buildAndPatch(fresh as typeof instance)
          },
          {
            enabled: refetchOnConflict,
            maxAttempts: refetchOnConflictMaxAttempts,
            resourceRef: `${instance.fhirType}/${instance.id}`,
          }
        )
      }

    getPreviousVersion: SeenMedplumClientExtension['getPreviousVersion'] =
      async <T extends Resource>(
        resource: T | ExtendedResource<T>
      ): Promise<T | undefined> => {
        invariant(resource.id, 'Resource must have an id')
        const resourceType =
          resource instanceof ExtendedResource
            ? resource.fhirType
            : resource.resourceType
        const history = await this.readHistory(resourceType, resource.id)
        return getPreviousVersionFromHistory<Resource>(history, resource) as
          | T
          | undefined
      }

    readResourceClass: SeenMedplumClientExtension['readResourceClass'] = async (
      resourceClass,
      id,
      options
    ) => {
      const resolvedId = extractId(id)
      invariant(resolvedId, 'Resource ID is required')
      const type = resourceClass.RESOURCE_TYPE
      const result = await this.readResource(type, resolvedId, options)
      return resourceClass.fromFhirResource(
        result as ResourceOf<InstanceType<typeof resourceClass>>
      )
    }

    readReferenceClass: SeenMedplumClientExtension['readReferenceClass'] =
      async (resourceClass, reference, options) => {
        const result = await this.readReference(reference, options)
        return resourceClass.fromFhirResource(
          result as ResourceOf<InstanceType<typeof resourceClass>>
        )
      }

    readHistoryClass: SeenMedplumClientExtension['readHistoryClass'] = async (
      resourceClass,
      id,
      options
    ) => {
      const result = await this.readHistory(
        resourceClass.RESOURCE_TYPE,
        id,
        options
      )
      return (
        result.entry
          ?.map((e) => e.resource)
          .filter((e) => !!e)
          .map((e) =>
            resourceClass.fromFhirResource(
              e as ResourceOf<InstanceType<typeof resourceClass>>
            )
          ) ?? []
      )
    }

    readVersionClass: SeenMedplumClientExtension['readVersionClass'] = async (
      resourceClass,
      id,
      versionId,
      options
    ) => {
      const result = await this.readVersion(
        resourceClass.RESOURCE_TYPE,
        id,
        versionId,
        options
      )
      return resourceClass.fromFhirResource(
        result as ResourceOf<InstanceType<typeof resourceClass>>
      )
    }

    searchResourceClass: SeenMedplumClientExtension['searchResourceClass'] =
      async (resourceClass, query, options) => {
        const type = resourceClass.RESOURCE_TYPE
        const result = await this.searchResources(
          type,
          normalizeQuery(query),
          options
        )
        return result.map((resource) =>
          resourceClass.fromFhirResource(
            resource as ResourceOf<InstanceType<typeof resourceClass>>
          )
        )
      }

    searchOneResourceClass: SeenMedplumClientExtension['searchOneResourceClass'] =
      async (resourceClass, query, options) => {
        const type = resourceClass.RESOURCE_TYPE
        const result = await this.searchOne(
          type,
          normalizeQuery(query),
          options
        )
        return result
          ? resourceClass.fromFhirResource(
              result as ResourceOf<InstanceType<typeof resourceClass>>
            )
          : null
      }

    searchResourceClassPages: SeenMedplumClientExtension['searchResourceClassPages'] =
      async function* <T extends ExtendedResource>(
        this: MedplumClient,
        resourceClass: ExtendedResourceConstructor<T>,
        query: QueryTypes | undefined,
        options?: MedplumRequestOptions
      ): AsyncGenerator<T[]> {
        const type = resourceClass.RESOURCE_TYPE
        const pages = this.searchResourcePages(
          type,
          normalizeQuery(query),
          options
        )
        for await (const page of pages) {
          yield page.map((resource) =>
            resourceClass.fromFhirResource(resource as ResourceOf<T>)
          )
        }
      }

    searchResourceClassPagesAll: SeenMedplumClientExtension['searchResourceClassPagesAll'] =
      async function <T extends ExtendedResource>(
        this: MedplumClient,
        resourceClass: ExtendedResourceConstructor<T>,
        query: QueryTypes | undefined,
        options?: MedplumRequestOptions
      ): Promise<T[]> {
        const type = resourceClass.RESOURCE_TYPE
        const result: T[] = []
        for await (const page of this.searchResourcePages(
          type,
          normalizeQuery(query),
          options
        )) {
          result.push(
            ...page.map((resource) =>
              resourceClass.fromFhirResource(resource as ResourceOf<T>)
            )
          )
        }
        return result
      }

    searchResourceClassPagesCursor: SeenMedplumClientExtension['searchResourceClassPagesCursor'] =
      async function* <T extends ExtendedResource>(
        this: MedplumClient,
        resourceClass: ExtendedResourceConstructor<T>,
        query: QueryTypes | undefined,
        options?: MedplumRequestOptions
      ): AsyncGenerator<T[]> {
        const type = resourceClass.RESOURCE_TYPE
        const params = new URLSearchParams(normalizeQuery(query))
        // Medplum only serves a cursor for _lastUpdated ascending, and rejects
        // a _count below 20. Override rather than trust the caller's query.
        params.set('_sort', '_lastUpdated')
        const count = Number(params.get('_count'))
        if (!Number.isFinite(count) || count < MIN_CURSOR_PAGE_SIZE) {
          params.set('_count', String(MIN_CURSOR_PAGE_SIZE))
        }

        for await (const page of this.searchResourcePages(
          type,
          params,
          options
        )) {
          yield page.map((resource) =>
            resourceClass.fromFhirResource(resource as ResourceOf<T>)
          )
        }
      }

    /**
     * Downloads the attachment using the blob id. This is necessary right now becausee there's no
     * CORS header enabled for storage.medplum.com
     * Relevant thread: https://seenhealth.slack.com/archives/C078NUJP6B1/p1737126003949229
     * @param url - The URL to request. Can be a standard URL or one in the form of `Binary/{id}`.
     * @param options - Optional fetch request init options.
     * @returns Promise to the response body as a blob.
     */
    async downloadAttachmentAsBinary(
      url: string,
      options: MedplumRequestOptions = {}
    ): Promise<Blob> {
      const binaryId = getBinaryId(url)
      if (!binaryId) {
        throw new Error('Invalid url')
      }
      return this.download(`Binary/${binaryId}`, options)
    }

    upsertResourceClass: SeenMedplumClientExtension['upsertResourceClass'] =
      async (instance, query, options) => {
        const resourceClass =
          instance.constructor as ExtendedResourceConstructor<typeof instance>
        const fhirResource = instance.toFhirResource()

        const result = await this.upsertResource(
          fhirResource,
          queryTypesToString(query),
          options
        )

        return resourceClass.fromFhirResource(
          result as ResourceOf<typeof instance>
        )
      }

    createResourceClass: SeenMedplumClientExtension['createResourceClass'] =
      async (instance, options) => {
        const resourceClass =
          instance.constructor as ExtendedResourceConstructor<typeof instance>
        const fhirResource = instance.toFhirResource()

        const result = await this.createResource(fhirResource, options)

        return resourceClass.fromFhirResource(
          result as ResourceOf<typeof instance>
        )
      }

    createResourceClassIfNoneExist: SeenMedplumClientExtension['createResourceClassIfNoneExist'] =
      async (instance, query, options) => {
        const resourceClass =
          instance.constructor as ExtendedResourceConstructor<typeof instance>
        const fhirResource = instance.toFhirResource()

        const result = await this.createResourceIfNoneExist(
          fhirResource,
          queryTypesToString(query),
          options
        )

        return resourceClass.fromFhirResource(
          result as ResourceOf<typeof instance>
        )
      }

    onBehalfOf: SeenMedplumClientExtension['onBehalfOf'] = async (
      email,
      action
    ) => {
      const normalizedEmail = email.trim().toLowerCase()

      const result =
        await this.searchProjectMembershipByPractitionerEmail(normalizedEmail)

      invariant(result, `No project membership for ${normalizedEmail}`)
      const { membership } = result
      invariant(membership.id, 'Project membership must have an id')

      // https://www.medplum.com/docs/auth/methods/on-behalf-of
      const defaultHeaders = this.getDefaultHeaders()
      const previousHeader = defaultHeaders['x-medplum-on-behalf-of']
      defaultHeaders['x-medplum'] = 'extended'
      defaultHeaders['x-medplum-on-behalf-of'] =
        `ProjectMembership/${membership.id}`
      try {
        return await action(membership)
      } finally {
        if (previousHeader !== undefined) {
          defaultHeaders['x-medplum-on-behalf-of'] = previousHeader
        } else {
          delete defaultHeaders['x-medplum-on-behalf-of']
        }
        delete defaultHeaders['x-medplum']
      }
    }

    searchProjectMembershipByPractitionerEmail: SeenMedplumClientExtension['searchProjectMembershipByPractitionerEmail'] =
      async (email) => {
        const practitioner = await this.searchOne(
          'Practitioner',
          `email=${email}`
        )
        if (!practitioner) {
          return undefined
        }
        const membership = await this.searchOne(
          'ProjectMembership',
          `profile-type=Practitioner&profile=Practitioner/${practitioner.id}`
        )
        if (!membership) {
          return undefined
        }
        return { membership, practitioner }
      }
  }
}

export class SeenMedplumClient extends SeenMedplumClientMixin(MedplumClient) {
  // allows type-safety on the overridden constructor options
  constructor(options?: SeenMedplumClientOptions) {
    super(options)
  }
}
export class SeenMedplumBatchError extends Error {
  readonly errors: BundleErrorInfo[]

  constructor(message: string, errors: BundleErrorInfo[]) {
    super(
      `${message}\n${errors.map((e) => JSON.stringify(e.outcome)).join('\n')}`
    )
    this.errors = errors
  }
}

/**
 * Terminal error thrown by `runWithRefetchOnConflict` when the bounded
 * retry budget is fully spent. Tagged so `isVersionConflictError` can
 * short-circuit on it: the original 412 lives on `cause`, and without
 * the tag a one-level cause walk would mistake this terminal failure
 * for a retriable conflict — an outer `runWithRefetchOnConflict` (or any
 * future wrapper applying the same heuristic) would then run another
 * full retry budget against an already-exhausted operation.
 */
export class RetryExhaustedError extends Error {
  readonly isRetryExhausted = true
  constructor(message: string, cause: unknown) {
    super(message, { cause })
    this.name = 'RetryExhaustedError'
  }
}

// Maximum depth we traverse a `cause` chain when matching error shape.
// Bounded to defend against pathological wrapping and cycles; legitimate
// causes in this codebase nest at most 2-3 deep.
const MAX_CAUSE_DEPTH = 8

/**
 * Walks the `error.cause` chain (including `error` itself), invoking
 * `predicate` on each node. Returns `true` as soon as `predicate` returns
 * `true` for a node, or `false` if no node matches. Bounded by
 * `MAX_CAUSE_DEPTH` and a `seen` set to defend against cycles.
 */
function someInCauseChain(
  cause: unknown,
  predicate: (cause: unknown) => boolean
): boolean {
  const seen = new Set<unknown>()
  let node: unknown = cause
  let depth = 0
  while (node !== undefined && node !== null && depth < MAX_CAUSE_DEPTH) {
    if (typeof node === 'object') {
      if (seen.has(node)) {
        return false
      }
      seen.add(node)
    }
    if (predicate(node)) {
      return true
    }
    if (
      typeof node === 'object' &&
      'cause' in (node as object) &&
      (node as { cause: unknown }).cause !== node
    ) {
      node = (node as { cause: unknown }).cause
      depth++
      continue
    }
    return false
  }
  return false
}

function isRetryExhaustedError(cause: unknown): boolean {
  return (
    !!cause &&
    typeof cause === 'object' &&
    'isRetryExhausted' in cause &&
    (cause as { isRetryExhausted: unknown }).isRetryExhausted === true
  )
}

export function isVersionTestFailedOutcome(outcome: OperationOutcome): boolean {
  return !!outcome.issue?.some(
    (issue) =>
      issue.code === 'invalid' &&
      /^test failed/i.test(issue.details?.text ?? '')
  )
}

/**
 * Detects whether `error` originated from a `412 Conflict` versionId test
 * failure. Walks the full `cause` chain so wrapped errors (e.g. an outer
 * caller's contextual error wrapping `patchResource`'s thrown 412) still
 * resolve correctly.
 *
 * Returns `false` if any node in the chain is tagged `isRetryExhausted`
 * (e.g. `RetryExhaustedError`): those are terminal failures and must not
 * retrigger a retry budget, even if they ultimately wrap a 412.
 */
function isVersionConflictError(cause: unknown): boolean {
  // Terminal-tag check on full chain so a deeply-wrapped exhausted error
  // still short-circuits to `false`.
  if (someInCauseChain(cause, isRetryExhaustedError)) {
    return false
  }

  return someInCauseChain(cause, (node) => {
    try {
      return isVersionTestFailedOutcome(normalizeOperationOutcome(node))
    } catch {
      return false
    }
  })
}

/**
 * Runs `attempt` once. If it fails with a versionId test conflict, and
 * `enabled` is true, runs `retry` up to `maxAttempts` more times — each
 * retry is responsible for re-reading the latest resource and rebuilding
 * its patch operations against that fresh version.
 *
 * Used by `patchResourceProxy` and `patchResourceInstance` to share the
 * "re-read and re-apply mutate on conflict" loop.
 */
async function runWithRefetchOnConflict<T>(
  attempt: () => Promise<T>,
  retry: () => Promise<T>,
  {
    enabled,
    maxAttempts,
    resourceRef,
  }: { enabled: boolean; maxAttempts: number; resourceRef: string }
): Promise<T> {
  try {
    return await attempt()
  } catch (error) {
    if (!enabled || !isVersionConflictError(error)) {
      throw error
    }

    let lastError = error
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await retry()
      } catch (retryError) {
        lastError = retryError
        if (!isVersionConflictError(retryError)) {
          throw retryError
        }
      }
    }

    throw new RetryExhaustedError(
      `[${resourceRef}] Failed to refetch-and-retry patch after ${maxAttempts} attempts due to persistent concurrent modifications`,
      lastError
    )
  }
}

/**
 * Determines if patch operations are safe to apply by checking if the fields
 * being modified have changed between the current and latest versions.
 *
 * When safe, returns a filtered operations array with redundant array
 * initializations removed (e.g. `add /identifier []` when the array already
 * exists in `latest`). Per RFC 6902 §4.1, `add` on an existing path replaces
 * the value, so retrying the raw init would wipe data added by the concurrent
 * process. See https://www.rfc-editor.org/rfc/rfc6902#section-4.1
 *
 * @param current - The resource version we based our patch operations on
 * @param latest - The current latest version from the server
 * @param operations - The patch operations we want to apply
 * @returns Safe result with cleaned operations, or unsafe result with conflicting paths
 */
export function arePatchOperationsSafe<T extends Resource>({
  current,
  latest,
  operations,
}: {
  current: T
  latest: T
  operations: PatchOperation<ResourceType>[]
}):
  | { safe: true; operations: PatchOperation<ResourceType>[] }
  | { safe: false; conflictingPaths: string[] } {
  if (operations.length === 0) {
    return { safe: true, operations }
  }

  const conflictingPaths: string[] = []
  const redundantIndices = new Set<number>()

  // For each operation, check if the field being modified has changed
  for (let i = 0; i < operations.length; i++) {
    const op = operations[i]

    // Skip test operations as they're meta-operations
    if (op.op === 'test') {
      continue
    }

    const path = op.path
    const currentValue = getValueAtPath(current, path)
    const latestValue = getValueAtPath(latest, path)

    // Check if the value at this path has changed between versions
    const hasChanged = !isEqual(currentValue, latestValue)

    if (hasChanged && latestValue !== undefined) {
      // An 'add' of an empty array where the latest already has an array is a
      // redundant array initialization — the concurrent process already created
      // the array. We drop this op on retry so it doesn't wipe the existing
      // data (RFC 6902 §4.1 'add' replaces existing values,
      // https://www.rfc-editor.org/rfc/rfc6902#section-4.1). The subsequent '/-'
      // appends carry the actual data and are inherently safe.
      if (
        op.op === 'add' &&
        Array.isArray(op.value) &&
        op.value.length === 0 &&
        Array.isArray(latestValue)
      ) {
        redundantIndices.add(i)
        continue
      }

      // The field we want to modify has been changed by someone else
      conflictingPaths.push(path)
    }
  }

  if (conflictingPaths.length > 0) {
    return { safe: false, conflictingPaths }
  }

  // Filter out redundant array init operations for the retry
  const filteredOps =
    redundantIndices.size > 0
      ? operations.filter((_, i) => !redundantIndices.has(i))
      : operations

  return { safe: true, operations: filteredOps }
}

/**
 * Gets value at a JSON Pointer (RFC 6901) path in an object.
 * See https://www.rfc-editor.org/rfc/rfc6901
 */
function getValueAtPath(obj: any, path: string): any {
  if (path === '') {
    return obj
  }

  // Remove leading slash (JSON Pointer paths start with '/', but we don't need it for splitting)
  const cleanPath = path.startsWith('/') ? path.slice(1) : path

  // Split by '/' to get path segments, then decode JSON Pointer escape sequences
  // (RFC 6901 §4, https://www.rfc-editor.org/rfc/rfc6901#section-4).
  // JSON Pointer encodes special characters in property names:
  // - '~1' encodes '/' (so property names with slashes don't break path parsing)
  // - '~0' encodes '~' (so property names with tildes don't conflict with escape sequences)
  // We must decode in this order (~1 before ~0) because ~0 would also match the '0' in '~1'.
  // Example: "/foo~1bar/baz" represents property "foo/bar" followed by "baz"
  const parts = cleanPath.split('/').map((part) =>
    part
      .replace(/~1/g, '/') // Decode '/' from '~1'
      .replace(/~0/g, '~') // Decode '~' from '~0'
      // '-' is a special JSON Patch token meaning "append to array", pass through unchanged
      .replace(/^-$/, '-')
  )

  let current = obj
  for (let i = 0; i < parts.length; i++) {
    if (current === undefined || current === null) {
      return undefined
    }

    const part = parts[i]

    // Handle array append notation
    if (part === '-') {
      return undefined // '-' means append, so there's no current value
    }

    current = current[part]
  }

  return current
}

// Parses `resourceType` (+ optional id) out of a bundle entry's
// `request.url`. Supports all three FHIR shapes:
//   - `ResourceType/id`               → {resourceType, resourceId}
//   - `ResourceType`                  → {resourceType, resourceId: undefined}
//   - `ResourceType?identifier=...`   → {resourceType, resourceId: undefined}
// Used to enrich `onBundleRetry` observability events so RUM payloads carry
// per-entry attribution rather than just an opaque count.
function bundleEntryAttribution(
  entry: BundleEntry
): BundleRetryEntryAttribution | undefined {
  const url = entry.request?.url
  if (!url) {
    return undefined
  }
  const [pathPart] = url.split('?')
  const [resourceType, resourceId] = pathPart.split('/')
  if (!resourceType) {
    return undefined
  }
  return { resourceType, resourceId: resourceId || undefined }
}

// Identifies PATCH entries in a bundle whose `test /meta/versionId` op
// failed. Handles both error shapes:
//   - SeenMedplumBatchError with per-entry outcomes (HTTP 200 path)
//   - Top-level OperationOutcome with "test failed" (HTTP 4xx path)
// For top-level failures the server doesn't identify which entry failed,
// so we narrow candidates to PATCH entries that actually contain a
// `test /meta/versionId` op — entries without one can't have caused a
// versionId test-failure and don't belong in the retry set.
function findVersionTestFailedPatchEntries(
  cause: unknown,
  bundle: Bundle
): BundleEntry[] {
  if (cause instanceof SeenMedplumBatchError) {
    return cause.errors
      .filter(
        (e) =>
          e.entry?.request?.method === 'PATCH' &&
          isVersionTestFailedOutcome(e.outcome)
      )
      .map((e) => e.entry)
      .filter((e): e is BundleEntry => e !== undefined)
  }
  const outcome = normalizeOperationOutcome(cause)
  const isTestFailure = !!outcome.issue?.some(
    (issue) =>
      issue.code === 'invalid' &&
      /^test failed/i.test(issue.details?.text ?? '')
  )
  if (!isTestFailure) {
    return []
  }
  return (
    bundle.entry?.filter(
      (e) => e.request?.method === 'PATCH' && hasVersionIdTestOp(e)
    ) ?? []
  )
}

function hasVersionIdTestOp(entry: BundleEntry): boolean {
  const binary = entry.resource
  if (
    binary?.resourceType !== 'Binary' ||
    !binary.data ||
    binary.contentType !== 'application/json-patch+json'
  ) {
    return false
  }
  let ops: { op: string; path: string }[]
  try {
    ops = JSON.parse(base64Decode(binary.data))
  } catch {
    return false
  }
  return ops.some((op) => op.op === 'test' && op.path === '/meta/versionId')
}

// Stamp each PATCH response entry with the versioned reference its patch was
// finally conditioned on (`ResourceType/id/_history/{baseVersion}`). The
// request entries hold the resolved json-patch by now (fhirpath converted, any
// conflict rebased in place), so the `test /meta/versionId` value on
// `request.entry[i]` is the authoritative base version for `response.entry[i]`
// — batch responses map positionally to the request. The full reference is
// stamped (not just the version) because a transaction-response entry carries
// no request url for the consumer to recombine. Consumers read it back via
// `appliedFromVersionRefs`; non-PATCH, unconditional, and conditional-url
// entries are left untouched.
function stampBaseVersions(request: Bundle, response: Bundle): void {
  const requestEntries = request.entry ?? []
  const responseEntries = response.entry ?? []
  for (const [index, requestEntry] of requestEntries.entries()) {
    if (requestEntry.request?.method !== 'PATCH') {
      continue
    }
    const url = requestEntry.request.url
    // Only direct `ResourceType/id` urls resolve to a single version.
    if (!url || !/^[A-Za-z]+\/[\w-]+$/.test(url)) {
      continue
    }
    const baseVersion = patchEntryBaseVersion(requestEntry.resource)
    const responseEntry = responseEntries[index]
    if (!baseVersion || !responseEntry) {
      continue
    }
    responseEntry.response ??= { status: '200' }
    responseEntry.response.extension = [
      ...(responseEntry.response.extension ?? []).filter(
        (e) => e.url !== BATCH_ENTRY_BASE_VERSION_EXTENSION
      ),
      {
        url: BATCH_ENTRY_BASE_VERSION_EXTENSION,
        valueString: createReferenceVersionUrl({ reference: url }, baseVersion),
      },
    ]
  }
}
